import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { OptimizedImage } from '../utils/performance';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    investmentInterest: 'general'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          investmentInterest: 'general'
        });
      } else {
        setError(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try emailing us directly at info@octagon8.io');
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: "Email Us",
      content: "info@octagon8.io",
      subContent: "support@octagon8.io",
      gradient: "from-purple-500 to-violet-600"
    }
  ];

  const investmentOptions = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'nft', label: 'NFT Purchase' },
    { value: 'investment', label: 'Investment Opportunities' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'technical', label: 'Technical Support' }
  ];

  if (isSubmitted) {
    return (
      <section id="contact" className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-lg rounded-3xl p-12 border border-green-500/30">
              <CheckCircleIcon className="w-20 h-20 text-green-400 mx-auto mb-6" />
              <h3 className="text-4xl font-bold text-white mb-4">Message Sent!</h3>
              <p className="text-xl text-gray-300 mb-8">
                Thank you for your interest in Octagon Infinity. Our team will get back to you within 24 hours.
              </p>
              <motion.button
                onClick={() => setIsSubmitted(false)}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Send Another Message
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-16 sm:py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <OptimizedImage 
          src="https://images.unsplash.com/photo-1717786133648-f913fbabc963?auto=format&fit=crop&w=1920&q=80"
          alt="Contact Office Background"
          className="w-full h-full object-cover opacity-5"
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Ready to join the Octagon Infinity community? Have questions about our NFT collection or investment opportunities? 
            Our expert team is here to help you get started.
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Information */}
            <motion.div
              className="lg:col-span-1 space-y-6 lg:space-y-8"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 lg:mb-6">Contact Information</h3>
                <p className="text-gray-300 leading-relaxed mb-6 lg:mb-8 text-sm lg:text-base">
                  Reach out to our team for personalized assistance with your investment journey. 
                  We're committed to providing exceptional support every step of the way.
                </p>
              </div>

              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-3 lg:space-x-4 p-4 lg:p-6 bg-gray-800/50 backdrop-blur-lg rounded-2xl border border-gray-700 hover:border-amber-500 transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className={`w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r ${info.gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <info.icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-base lg:text-lg font-semibold text-white mb-1">{info.title}</h4>
                    <p className="text-gray-300 text-sm lg:text-base">{info.content}</p>
                    <p className="text-gray-400 text-xs lg:text-sm">{info.subContent}</p>
                  </div>
                </motion.div>
              ))}


            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-800/50 backdrop-blur-lg rounded-3xl p-6 lg:p-8 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6 lg:mb-8">
                  <ChatBubbleLeftRightIcon className="w-6 h-6 lg:w-8 lg:h-8 text-amber-400" />
                  <h3 className="text-2xl lg:text-3xl font-bold text-white">Send us a Message</h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
                  <div className="grid md:grid-cols-2 gap-4 lg:gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none transition-colors duration-200"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none transition-colors duration-200"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">Subject</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none transition-colors duration-200"
                        placeholder="Subject of your inquiry"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">Investment Interest</label>
                      <select
                        name="investmentInterest"
                        value={formData.investmentInterest}
                        onChange={handleInputChange}
                        className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors duration-200"
                      >
                        {investmentOptions.map((option) => (
                          <option key={option.value} value={option.value} className="bg-gray-800">
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none transition-colors duration-200 resize-none"
                      placeholder="Tell us about your interest in Octagon Infinity, questions about our NFT collection, or investment opportunities you'd like to explore..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-4 rounded-2xl text-xl font-semibold transition-all duration-300 ${
                      isLoading 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-amber-600 to-blue-600 hover:from-amber-700 hover:to-blue-700 text-white'
                    }`}
                    whileHover={!isLoading ? { scale: 1.02 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending Message...</span>
                      </div>
                    ) : (
                      'Send Message'
                    )}
                  </motion.button>
                  
                  {/* Error Message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-xl text-red-300"
                    >
                      {error}
                    </motion.div>
                  )}
                </form>

                <p className="text-sm text-gray-400 mt-6 text-center">
                  By submitting this form, you agree to our Privacy Policy and Terms of Service. 
                  We'll never share your information with third parties.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <motion.footer
          className="mt-16 lg:mt-20 pt-8 lg:pt-12 border-t border-gray-800"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-8 lg:mb-12">
            <div className="sm:col-span-2">
              <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-yellow-400 to-blue-400 bg-clip-text text-transparent mb-4">
                Octagon infinity
              </div>
              <p className="text-gray-400 leading-relaxed max-w-md text-sm lg:text-base">
                Revolutionizing European real estate investment through exclusive NFT access and blockchain transparency. 
                Join the future of premium property investment.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4 text-base lg:text-lg">Quick Links</h4>
              <div className="space-y-2">
                {['Home', 'Mission', 'Projects', 'Calculator', 'Team', 'Community'].map((link) => (
                  <button
                    key={link}
                    onClick={() => {
                      const sectionId = link === 'Home' ? 'hero' : 
                                       link === 'Community' ? 'social' : 
                                       link.toLowerCase();
                      document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="block text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4 text-base lg:text-lg">Legal</h4>
              <div className="space-y-2">
                <a 
                  href="/whitepaper" 
                  className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm lg:text-base"
                >
                  Whitepaper
                </a>
                <a 
                  href="/privacy-policy" 
                  className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm lg:text-base"
                >
                  Privacy Policy
                </a>
                <a 
                  href="/terms-of-service" 
                  className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm lg:text-base"
                >
                  Terms of Service
                </a>
                <a 
                  href="/risk-disclosure" 
                  className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm lg:text-base"
                >
                  Risk Disclosure
                </a>
              </div>
            </div>
          </div>

          <div className="text-center py-6 lg:py-8 border-t border-gray-800">
            <p className="text-gray-400 text-sm lg:text-base">
              © 2025 Octagon Infinity. All rights reserved. | Built with ❤️ for the future of investment.
            </p>
          </div>
        </motion.footer>
      </div>
    </section>
  );
};

export default ContactSection;